import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Checkbox,
  FormControlLabel, Select, MenuItem, FormControl, InputLabel, FormHelperText, Alert, Typography,
} from '@mui/material';
import FlexService from '../../services/FlexService';
import { enterAdvanceProps } from '../../utils/enterAdvance';
import PermissionService from '../../services/PermissionService';
import { humanizeEntityName } from '../../utils/entityLabels';

/**
 * "Add custom field" (FH-202): adds a field of the customer's own to an entity, from that
 * entity's form. custom_field_add defines it in the WMS and adds it to this warehouse's
 * field metadata, so the form, the grid and the details show it from then on.
 */

/** Window event fired after an entity's fields change: detail = { entityName }. */
export const ENTITY_FIELDS_CHANGED = 'fast-entity-fields-changed';

// Entities whose WMS API saves and checks custom field values (FH-202).
const CUSTOM_FIELD_ENTITIES = ['Item', 'Receipt', 'ReceiptLine', 'Order', 'OrderLine', 'Inventory'];

/** Whether a field is one a customer added (its value travels in the record's customField map). */
export const isCustomField = (f) => (f?.wmsPayloadPath || '').startsWith('customField.');

/**
 * A form that lists its fields by name can't list fields a customer adds later: this puts the
 * entity's custom fields (from its metadata, `all`) after the listed ones, in their own order.
 */
export const withCustomFields = (listed, all) => {
  const have = new Set(listed.map((f) => f.fieldName));
  const extra = (all || [])
    .filter((f) => isCustomField(f) && f.isVisibleInForm !== false && !have.has(f.fieldName))
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  return extra.length > 0 ? [...listed, ...extra] : listed;
};

/** Custom field values go to the WMS as one map: "customField.color" -> customField: { color }. */
export const foldCustomFields = (payload) => {
  Object.keys(payload).filter((k) => k.startsWith('customField.')).forEach((k) => {
    payload.customField = { ...(payload.customField || {}), [k.slice('customField.'.length)]: payload[k] };
    delete payload[k];
  });
  return payload;
};

// Whether the signed-in user may add custom fields (Admin and Supervisor by default) -- the
// same check the server makes when custom_field_add runs, cached for the page.
let permission = null;
export const canAddCustomField = (entityName) => {
  if (!CUSTOM_FIELD_ENTITIES.includes(entityName)) return Promise.resolve(false);
  if (!permission) permission = PermissionService.canAccess('FLEX_SCRIPT', 'custom_field_add');
  return permission;
};

const TYPES = [
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number (whole)' },
  { value: 'YESNO', label: 'Yes/No' },
  { value: 'PICKLIST', label: 'Pick list' },
];

// The entities a field can be added to, as a person names them
const ENTITY_CHOICES = [
  { value: 'Item', label: 'Items' },
  { value: 'Receipt', label: 'Receipts' },
  { value: 'ReceiptLine', label: 'Receipt lines' },
  { value: 'Order', label: 'Orders' },
  { value: 'OrderLine', label: 'Order lines' },
  { value: 'Inventory', label: 'Inventory', noun: 'inventory record' },
];

const empty = { entity: '', label: '', fieldType: 'TEXT', isRequired: false, maxLength: '', options: '', rule: '' };
// types a rule can check (FH-253); a Yes/No or a pick list already limits the value
const RULE_TYPES = ['TEXT', 'NUMBER'];

/**
 * Add a field (from an entity's own form, or from System Config with no entity yet: then the
 * dialog asks which one), or change one (`field`, a row of custom_field_list: FH-205). A field's
 * entity and type stay what they are; its name, required, max length and pick list values change.
 */
const AddCustomFieldDialog = ({ open, entityName, entityLabel, field, colors = {}, onCancel, onAdded }) => {
  const editing = !!field;
  const [values, setValues] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ruleChoices, setRuleChoices] = useState([]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setValues(editing ? {
      entity: field.entityName,
      label: field.label || '',
      fieldType: field.fieldType || 'TEXT',
      isRequired: !!field.isRequired,
      maxLength: field.maxLength == null ? '' : String(field.maxLength),
      options: field.options || '',
      rule: field.rule || '',
    } : { ...empty, entity: entityName || '' });
    // the rules a field can carry (FH-253): FLEX Commands the form runs on the value
    FlexService.executeScript({ scriptName: 'custom_field_rule_options', context: { _payload: {} } })
      .then((resp) => { const r = resp?.context?.result; setRuleChoices(Array.isArray(r) ? r : []); })
      .catch(() => setRuleChoices([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const entity = values.entity;

  const save = async () => {
    if (!entity) {
      setError('Choose what the field is for.');
      return;
    }
    if (!values.label.trim()) {
      setError('Give the field a name.');
      return;
    }
    setSaving(true);
    setError(null);
    const rules = {
      label: values.label.trim(),
      isRequired: values.fieldType !== 'YESNO' && values.isRequired,
      maxLength: values.fieldType === 'TEXT' ? String(values.maxLength || '').trim() : '',
      options: values.fieldType === 'PICKLIST' ? values.options : '',
      rule: RULE_TYPES.includes(values.fieldType) ? (values.rule || '') : '',
    };
    try {
      const resp = await FlexService.executeScript(editing ? {
        scriptName: 'custom_field_edit',
        context: { _payload: { entityName: entity, fieldName: field.fieldName, ...rules } },
      } : {
        scriptName: 'custom_field_add',
        context: { _payload: { entityName: entity, fieldType: values.fieldType, ...rules } },
      });
      const ctx = resp?.context || {};
      if (!resp?.success || ctx.success === false) {
        setError(ctx.error_message || resp?.diagnostics?.[0]?.message || (editing ? "The change wasn't saved." : "The field wasn't added."));
        return;
      }
      // grids and forms showing this entity re-read its fields, so the change shows without a reload
      window.dispatchEvent(new CustomEvent(ENTITY_FIELDS_CHANGED, { detail: { entityName: entity } }));
      onAdded(ctx.result || {});
    } catch (e) {
      setError(e.message || (editing ? "The change wasn't saved." : "The field wasn't added."));
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = { mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 0 } };
  const choice = ENTITY_CHOICES.find((c) => c.value === entity);
  const noun = (entityLabel || '').toLowerCase()
    || (choice ? (choice.noun || choice.label.toLowerCase().replace(/s$/, '')) : '')
    || (entity ? humanizeEntityName(entity).toLowerCase() : '');
  let intro = 'Adds a field of your own, for everyone who works with them. It shows on their forms, lists and details.';
  if (editing) {
    intro = `Changes this field for every ${noun}. Values already entered stay. A pick list value you take out stays on the ${noun}s that have it.`;
  } else if (entityName) {
    intro = `Adds a field of your own to every ${noun}, for everyone who works with them. It appears on this form straight away, ready to fill in, and in the list and details. Nothing you entered here is lost.`;
  }
  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 0, bgcolor: colors.surface || colors.background } }}>
      <DialogTitle sx={{ color: colors.textPrimary }}>{editing ? 'Change a custom field' : 'Add a custom field'}</DialogTitle>
      <DialogContent {...enterAdvanceProps(() => { if (!saving) save(); })}>
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>{intro}</Typography>
        {(!entityName || editing) && (
          <FormControl fullWidth size="small" sx={fieldSx} disabled={editing}>
            <InputLabel id="custom-field-entity-label">Add it to *</InputLabel>
            <Select labelId="custom-field-entity-label" label="Add it to *" value={entity}
              onChange={(e) => set('entity', e.target.value)}>
              {ENTITY_CHOICES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <TextField
          fullWidth size="small" autoFocus={!!entityName || editing}
          label="Field name *"
          value={values.label}
          onChange={(e) => set('label', e.target.value)}
          helperText="What people will see, such as Color or Pack Size. Letters, numbers and spaces."
          inputProps={{ maxLength: 40 }}
          sx={fieldSx}
        />
        <FormControl fullWidth size="small" sx={fieldSx} disabled={editing}>
          <InputLabel id="custom-field-type-label">Type</InputLabel>
          <Select labelId="custom-field-type-label" label="Type" value={values.fieldType}
            onChange={(e) => set('fieldType', e.target.value)}>
            {TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>
        {values.fieldType === 'TEXT' && (
          <TextField
            fullWidth size="small" type="number"
            label="Max length"
            value={values.maxLength}
            onChange={(e) => set('maxLength', e.target.value)}
            helperText="The most characters allowed. Leave blank for no limit."
            inputProps={{ min: 1 }}
            sx={fieldSx}
          />
        )}
        {values.fieldType === 'PICKLIST' && (
          <TextField
            fullWidth size="small"
            label="Values to choose from *"
            value={values.options}
            onChange={(e) => set('options', e.target.value)}
            helperText="Separate them with commas, such as Red, Blue, Green."
            sx={fieldSx}
          />
        )}
        {RULE_TYPES.includes(values.fieldType) && ruleChoices.length > 0 && (
          <FormControl fullWidth size="small" sx={fieldSx}>
            <InputLabel id="custom-field-rule-label">Rule</InputLabel>
            <Select labelId="custom-field-rule-label" label="Rule" value={values.rule}
              onChange={(e) => set('rule', e.target.value)}>
              <MenuItem value="">No rule</MenuItem>
              {ruleChoices.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
            <FormHelperText>
              {(ruleChoices.find((r) => r.value === values.rule) || {}).description
                || 'A check on what people type. A value that breaks it is refused on the form, with the reason.'}
            </FormHelperText>
          </FormControl>
        )}
        {values.fieldType !== 'YESNO' && (
          <FormControlLabel
            control={<Checkbox checked={values.isRequired} onChange={(e) => set('isRequired', e.target.checked)} />}
            label={`Required: every new ${noun || 'record'} must have it`}
          />
        )}
        {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving}
          sx={{ textTransform: 'none', borderRadius: 0, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
        >
          {saving ? 'Saving...' : (editing ? 'Save changes' : 'Add field')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCustomFieldDialog;
